import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Form, Modal, Alert, Badge } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus as FaPlus, Trash2 as FaTrash, GraduationCap as FaUserGraduate, UserRound as FaUserTie, ChevronLeft as FaChevronLeft, ChevronRight as FaChevronRight } from 'lucide-react';
import api from '../../api/axios';
import Loader from '../../components/Loader';
import GradientButton from '../../components/GradientButton';

const VACATION_TYPES = {
  wypoczynkowy: { color: '#0d6efd', bg: '#e7f1ff', label: 'Wypoczynkowy' },
  okolicznościowy: { color: '#f59f00', bg: '#fff9db', label: 'Okolicznościowy' },
  bezpłatny: { color: '#e03131', bg: '#fff5f5', label: 'Bezpłatny' },
};

const MONTHS_PL = [
  'Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec',
  'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień'
];
const DAYS_PL = ['Pn', 'Wt', 'Śr', 'Cz', 'Pt', 'So', 'Nd'];

function toDateStr(date) {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function formatDate(s) {
  return new Date(s).toLocaleDateString('pl-PL');
}

function countWorkingDays(start, end) {
  let count = 0;
  const cur = new Date(start);
  cur.setHours(12, 0, 0, 0);
  const endD = new Date(end);
  endD.setHours(12, 0, 0, 0);
  while (cur <= endD) {
    const d = cur.getDay();
    if (d !== 0 && d !== 6) count++;
    cur.setDate(cur.getDate() + 1);
  }
  return count;
}

function MiniCalendar({ year, month, vacations, selStart, selEnd, onDayClick, onDayHover }) {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  let startOffset = firstDay.getDay() - 1;
  if (startOffset < 0) startOffset = 6;

  const days = [];
  for (let i = 0; i < startOffset; i++) days.push(null);
  for (let d = 1; d <= lastDay.getDate(); d++) days.push(d);

  const getVac = (day) => {
    if (!day) return null;
    const ds = toDateStr(new Date(year, month, day));
    return vacations.find(v => ds >= v.start_date && ds <= v.end_date);
  };

  const inSel = (day) => {
    if (!day || !selStart) return false;
    const ds = toDateStr(new Date(year, month, day));
    const end = selEnd || selStart;
    const [a, b] = selStart <= end ? [selStart, end] : [end, selStart];
    return ds >= a && ds <= b;
  };

  const isWeekend = (day) => {
    if (!day) return false;
    const d = new Date(year, month, day).getDay();
    return d === 0 || d === 6;
  };

  const isToday = (day) => {
    if (!day) return false;
    const t = new Date();
    return day === t.getDate() && month === t.getMonth() && year === t.getFullYear();
  };

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: '2px', marginBottom: '4px' }}>
        {DAYS_PL.map(d => (
          <div key={d} style={{
            textAlign: 'center', fontSize: '11px', fontWeight: 600, padding: '4px 0',
            color: d === 'So' || d === 'Nd' ? '#e03131' : '#868e96',
          }}>{d}</div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: '3px' }}>
        {days.map((day, idx) => {
          const vac = getVac(day);
          const wknd = isWeekend(day);
          const sel = inSel(day) && !wknd;
          const today = isToday(day);

          let bg = 'transparent';
          let color = '#212529';

          if (!day) {
            color = 'transparent';
          } else if (wknd) {
            bg = '#f8f9fa';
            color = '#ced4da';
          } else if (vac) {
            bg = VACATION_TYPES[vac.vacation_type]?.color || '#0d6efd';
            color = 'white';
          } else if (sel) {
            bg = '#4dabf7';
            color = 'white';
          } else if (today) {
            bg = '#e9ecef';
            color = '#212529';
          } else {
            bg = '#f8f9fa';
            color = '#212529';
          }

          return (
            <div
              key={idx}
              onClick={() => day && !wknd && onDayClick(toDateStr(new Date(year, month, day)))}
              onMouseEnter={() => day && !wknd && onDayHover(toDateStr(new Date(year, month, day)))}
              style={{
                position: 'relative', aspectRatio: '1',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                borderRadius: '8px', fontSize: '13px', userSelect: 'none',
                cursor: day && !wknd ? 'pointer' : 'default',
                fontWeight: vac || sel ? 600 : 400,
                background: bg, color,
                border: today && !vac && !sel ? '1px solid #dee2e6' : '1px solid transparent',
              }}
            >
              {day}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function EmployerVacations() {
  const [searchParams] = useSearchParams();
  const preselectedWorkerId = searchParams.get('worker');
  const preselectedSource = searchParams.get('source') || 'managed';

  const [workers, setWorkers] = useState([]);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [loading, setLoading] = useState(true);
  const [vacationsLoading, setVacationsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [vacationData, setVacationData] = useState(null);

  const now = new Date();
  const [viewYear, setViewYear] = useState(now.getFullYear());
  const [viewMonth, setViewMonth] = useState(now.getMonth());
  const [selStart, setSelStart] = useState(null);
  const [selHover, setSelHover] = useState(null);
  const [vacType, setVacType] = useState('wypoczynkowy');

  const [showAddModal, setShowAddModal] = useState(false);
  const [pendingRange, setPendingRange] = useState(null);
  const [notes, setNotes] = useState('');

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [vacationToDelete, setVacationToDelete] = useState(null);



  const fadeInUp = {
    initial: { opacity: 0, y: 40 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5 }
  };

useEffect(() => {
    const fetchWorkers = async () => {
      setLoading(true);
      try {
        const [managedResult, linkedResult] = await Promise.allSettled([
          api.get('/employer/workers/'),
          api.get('/employer/linked-workers/accepted/'),
        ]);

        const managed = managedResult.status === 'fulfilled'
          ? (Array.isArray(managedResult.value.data) ? managedResult.value.data : (managedResult.value.data?.results ?? []))
              .map(w => ({ ...w, source: 'managed' }))
          : [];

        if (managedResult.status === 'rejected') console.error('managed workers error:', managedResult.reason);

        const linked = linkedResult.status === 'fulfilled'
          ? (linkedResult.value.data || []).map(link => ({
              id: link.id,
              source: 'linked',
              worker_type: 'employee',
              first_name: link.worker_first_name,
              last_name: link.worker_last_name,
              vacation_days_available: link.vacation_days_available,
            }))
          : [];

        if (linkedResult.status === 'rejected') console.error('linked workers error:', linkedResult.reason);

        const list = [...managed, ...linked];
        setWorkers(list);

        if (preselectedWorkerId) {
          const found = list.find(w => String(w.id) === String(preselectedWorkerId) && w.source === preselectedSource);
          if (found) setSelectedWorker(found);
          else if (list.length > 0) setSelectedWorker(list[0]);
        } else if (list.length > 0) {
          setSelectedWorker(list[0]);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchWorkers();
  }, [preselectedWorkerId, preselectedSource]);

  const fetchVacations = useCallback(async () => {
      if (!selectedWorker) return;
      setVacationsLoading(true);
      try {
        const url = selectedWorker.source === 'linked'
          ? `/employer/linked-workers/${selectedWorker.id}/vacation-calendar/`
          : `/employer/workers/${selectedWorker.id}/vacations/`;
        const res = await api.get(url);
        setVacationData(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setVacationsLoading(false);
      }
    }, [selectedWorker]);

  useEffect(() => {
    fetchVacations();
    setSelStart(null);
    setSelHover(null);
  }, [fetchVacations]);

  const prevMonth = () => { if (viewMonth === 0) { setViewYear(y => y - 1); setViewMonth(11); } else setViewMonth(m => m - 1); };
  const nextMonth = () => { if (viewMonth === 11) { setViewYear(y => y + 1); setViewMonth(0); } else setViewMonth(m => m + 1); };

  const handleDayClick = (ds) => {
      if (selectedWorker?.source === 'linked') return; // tylko podgląd — urlop tworzy pracownik przez wniosek

      if (!selStart) {
        setSelStart(ds);
      } else {
        const [s, e] = selStart <= ds ? [selStart, ds] : [ds, selStart];
        setPendingRange({ start: s, end: e });
        setShowAddModal(true);
        setSelStart(null);
        setSelHover(null);
      }
    };

  const handleConfirmAdd = async () => {
      if (!pendingRange || !selectedWorker) return;
      setError('');
      try {
        await api.post(`/employer/workers/${selectedWorker.id}/vacations/`, {
          start_date: pendingRange.start,
          end_date: pendingRange.end,
          vacation_type: vacType,
          notes,
        });
        setSuccess('Urlop został dodany');
        setShowAddModal(false);
        setPendingRange(null);
        setNotes('');

        await fetchVacations();
        const res = await api.get('/employer/workers/');
        const list = Array.isArray(res.data) ? res.data : (res.data?.results ?? []);
        setWorkers(list);
        const updated = list.find(w => w.id === selectedWorker.id);
        if (updated) setSelectedWorker(updated);

        setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
        setError(
          err.response?.data?.detail
          || err.response?.data?.non_field_errors?.[0]
          || 'Nie udało się dodać urlopu'
        );
      }
    };

  const handleDeleteClick = (vacation) => {
    setVacationToDelete(vacation);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
      if (!vacationToDelete || !selectedWorker) return;
      if (selectedWorker.source === 'linked') {
        try {
          await api.post(`/employer/vacation-requests/${vacationToDelete.id}/cancel/`);
          setSuccess('Urlop został odwołany');
          await fetchVacations();
          setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
          setError(err.response?.data?.detail || 'Nie udało się odwołać urlopu');
        }
        setShowDeleteModal(false);
        setVacationToDelete(null);
        return;
      }
      try {
        await api.delete(`/employer/workers/${selectedWorker.id}/vacations/${vacationToDelete.id}/`);
        setSuccess('Urlop został usunięty');

        // Odśwież zarówno urlopy jak i listę pracowników (tak jak przy dodawaniu)
        await fetchVacations();
        const res = await api.get('/employer/workers/');
        const list = Array.isArray(res.data) ? res.data : (res.data?.results ?? []);
        setWorkers(list);
        const updated = list.find(w => w.id === selectedWorker.id);
        if (updated) setSelectedWorker(updated);

        setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
        setError('Nie udało się usunąć urlopu');
      }
      setShowDeleteModal(false);
      setVacationToDelete(null);
    };

  if (loading) return <Loader />;

  if (workers.length === 0) {
    return (
      <div style={{ minHeight: '100vh', paddingTop: '2rem', paddingBottom: '4rem' }}>
        <Container fluid className="px-2 px-md-4">
          <Card
            className="border-0 shadow-lg text-center"
            style={{ padding: 0 }}
          >
            <Card.Body className="p-5">
              <h3 className="pub-h3 mb-3">Brak pracowników</h3>
              <p className="pub-muted mb-4">Dodaj pracownika w zakładce "Pracownicy", żeby zarządzać jego urlopem.</p>
              <GradientButton gradient="blue" onClick={() => window.location.href = '/employer-employees'}>
                Przejdź do pracowników
              </GradientButton>
            </Card.Body>
          </Card>
        </Container>
      </div>
    );
  }

  const vacations = vacationData?.vacations || [];
  const totalDays = vacationData?.vacation_days_total ?? 0;
  const usedDays = vacationData?.vacation_days_used ?? 0;
  const availableDays = vacationData?.vacation_days_available ?? 0;

  return (
    <div style={{ minHeight: '100vh', paddingTop: '2rem', paddingBottom: '4rem' }}>
      <Container fluid className="px-2 px-md-4">

        <motion.div {...fadeInUp} className="mb-4">
          <h1 className="pub-h2 mb-1">Urlopy pracowników</h1>
          <p className="pub-muted mb-0">Zarządzaj urlopami i kontroluj wykorzystanie puli</p>
        </motion.div>

        {success && <Alert variant="success" className="border-0" style={{ borderRadius: '15px' }}>{success}</Alert>}
        {error && !showAddModal && <Alert variant="danger" className="border-0" style={{ borderRadius: '15px' }}>{error}</Alert>}

        <Row className="g-4">
          {/* LISTA PRACOWNIKÓW */}
          <Col xs={12} lg={3}>
            <motion.div {...fadeInUp}>
              <Card className="border-0 shadow-lg" style={{ borderRadius: '20px', overflow: 'hidden' }}>
                <Card.Header style={{ padding: '1.25rem' }}>
                  <h6 className="mb-0 fw-bold">Pracownicy ({workers.length})</h6>
                </Card.Header>
                <Card.Body className="p-2" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                  {workers.map(worker => (
                    <div
                      key={`${worker.source}-${worker.id}`}
                      onClick={() => setSelectedWorker(worker)}
                      className="d-flex align-items-center gap-2 p-2 mb-1"
                      style={{
                        borderRadius: '12px', cursor: 'pointer',
                        background: selectedWorker?.id === worker.id ? '#e7f1ff' : 'transparent',
                        transition: 'background 0.15s',
                      }}
                    >
                      <div style={{
                        width: '34px', height: '34px', borderRadius: '50%', flexShrink: 0,
                        background: 'var(--pub-navy)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: 'white', fontWeight: 700, fontSize: '13px',
                      }}>
                        {worker.first_name?.[0]}{worker.last_name?.[0]}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div className="fw-medium text-truncate" style={{ fontSize: '13px', color: '#212529' }}>
                          {worker.first_name} {worker.last_name}
                        </div>
                        <div style={{ fontSize: '11px', color: '#868e96' }}>
                          {worker.vacation_days_available} dni dostępne
                        </div>
                      </div>
                    </div>
                  ))}
                </Card.Body>
              </Card>
            </motion.div>
          </Col>

          {/* KALENDARZ I PULA */}
          <Col xs={12} lg={9}>
            {vacationsLoading ? (
              <Loader />
            ) : selectedWorker && (
              <motion.div {...fadeInUp}>
                <Card className="border-0 shadow-lg mb-4" style={{ borderRadius: '20px', overflow: 'hidden' }}>
                  <Card.Header
                    className="d-flex align-items-center justify-content-between flex-wrap gap-2"
                    style={{ padding: '1.25rem' }}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <h5 className="mb-0 fw-bold">{selectedWorker.first_name} {selectedWorker.last_name}</h5>
                    </div>
                  </Card.Header>
                  <Card.Body className="p-4">

                    {/* Pula urlopowa */}
                    <Row className="g-3 mb-4">
                      {[
                        { label: 'Przyznane', value: totalDays, bg: '#f1f3f5', color: '#212529' },
                        { label: 'Użyte', value: usedDays, bg: '#fff3e0', color: '#e67700' },
                        { label: 'Dostępne', value: availableDays, bg: '#e8f5e9', color: '#2b8a3e' },
                      ].map(stat => (
                        <Col xs={4} key={stat.label}>
                          <div className="p-3 text-center" style={{ background: stat.bg, borderRadius: '14px' }}>
                            <div className="fw-bold" style={{ fontSize: '1.8rem', color: stat.color }}>{stat.value}</div>
                            <small style={{ color: '#868e96' }}>{stat.label}</small>
                          </div>
                        </Col>
                      ))}
                    </Row>

                    {/* Nawigacja miesiąca */}
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <button onClick={prevMonth} className="btn btn-light" style={{ borderRadius: '10px' }}>
                        <FaChevronLeft />
                      </button>
                      <span className="fw-bold" style={{ fontSize: '1.1rem', color: 'var(--pub-navy)' }}>
                        {MONTHS_PL[viewMonth]} {viewYear}
                      </span>
                      <button onClick={nextMonth} className="btn btn-light" style={{ borderRadius: '10px' }}>
                        <FaChevronRight />
                      </button>
                    </div>

                    <MiniCalendar
                      year={viewYear} month={viewMonth} vacations={vacations}
                      selStart={selStart} selEnd={selStart ? (selHover || selStart) : null}
                      onDayClick={handleDayClick} onDayHover={ds => selStart && setSelHover(ds)}
                    />
                    {selectedWorker?.source === 'linked' && (
                      <Alert variant="light" className="border mt-3" style={{ borderRadius: '12px', color: '#495057' }}>
                        ℹ️ Ten pracownik samodzielnie składa wnioski urlopowe do akceptacji — kalendarz pokazuje tylko zaakceptowane terminy.
                      </Alert>
                    )}
                    {selStart && (
                      <Alert variant="info" className="border-0 mt-3" style={{ borderRadius: '12px' }}>
                        📅 Od: <strong>{formatDate(selStart)}</strong>
                        {selHover && selHover !== selStart && (
                          <>
                            {' '}→ <strong>{formatDate(selHover)}</strong>
                            {' '}— <strong>
                              {countWorkingDays(
                                ...(selStart <= selHover ? [selStart, selHover] : [selHover, selStart])
                              )} dni roboczych
                            </strong>
                          </>
                        )}
                        <br />
                        <small>Kliknij drugi dzień żeby dodać urlop — weekendy są pomijane</small>
                      </Alert>
                    )}

                    {/* Legenda */}
                    <div className="d-flex gap-3 flex-wrap mt-3">
                      {Object.entries(VACATION_TYPES).map(([key, val]) => (
                        <div key={key} className="d-flex align-items-center gap-1">
                          <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: val.color, display: 'inline-block' }} />
                          <small style={{ color: '#868e96' }}>{val.label}</small>
                        </div>
                      ))}
                    </div>
                  </Card.Body>
                </Card>

                {/* LISTA URLOPÓW */}
                <Card className="border-0 shadow-lg" style={{ borderRadius: '20px', overflow: 'hidden' }}>
                  <Card.Header style={{ background: '#f8f9fa', border: 'none', padding: '1.25rem' }}>
                    <h6 className="mb-0 fw-bold" style={{ color: '#212529' }}>Historia urlopów</h6>
                  </Card.Header>
                  <Card.Body className="p-3">
                    {vacations.length === 0 ? (
                      <p className="text-center text-muted py-4 mb-0">Brak zaplanowanych urlopów. Kliknij dzień w kalendarzu, żeby dodać.</p>
                    ) : (
                      <AnimatePresence>
                        {vacations.map(v => (
                          <motion.div
                            key={v.id}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 10 }}
                            className="d-flex align-items-center justify-content-between p-3 mb-2"
                            style={{
                              borderRadius: '14px',
                              background: VACATION_TYPES[v.vacation_type]?.bg || '#f8f9fa',
                              border: `1px solid ${VACATION_TYPES[v.vacation_type]?.color}33`,
                            }}
                          >
                            <div>
                              <div className="d-flex align-items-center gap-2 flex-wrap">
                                <span style={{ color: '#212529', fontWeight: 600, fontSize: '14px' }}>
                                  {formatDate(v.start_date)}
                                  {v.start_date !== v.end_date && ` – ${formatDate(v.end_date)}`}
                                </span>
                                <span className="d-flex align-items-center gap-1">
                                  <span style={{
                                    width: '10px', height: '10px', borderRadius: '50%',
                                    background: VACATION_TYPES[v.vacation_type]?.color, display: 'inline-block',
                                  }} />
                                  <small style={{ color: VACATION_TYPES[v.vacation_type]?.color, fontWeight: 600 }}>
                                    {VACATION_TYPES[v.vacation_type]?.label}
                                  </small>
                                </span>
                                <Badge style={{ background: VACATION_TYPES[v.vacation_type]?.color, fontSize: '11px' }}>
                                  {v.days_count} dni
                                </Badge>
                              </div>
                              {v.notes && <div className="text-muted small mt-1">{v.notes}</div>}
                            </div>
                            {(selectedWorker?.source !== 'linked' || v.start_date >= new Date().toISOString().slice(0, 10)) && (
                              <button
                                onClick={() => handleDeleteClick(v)}
                                className="btn btn-outline-danger btn-sm"
                                style={{ borderRadius: '8px' }}
                              >
                                <FaTrash size={12} />
                              </button>
                            )}
                          </motion.div>
                        ))}
                      </AnimatePresence>
                    )}
                  </Card.Body>
                </Card>
              </motion.div>
            )}
          </Col>
        </Row>

        {/* MODAL DODAWANIA URLOPU */}
        <Modal show={showAddModal} onHide={() => { setShowAddModal(false); setPendingRange(null); }} centered>
          <Modal.Header closeButton style={{ border: 'none' }}>
            <Modal.Title>Dodaj urlop</Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            {error && <Alert variant="danger" className="border-0" style={{ borderRadius: '12px' }}>{error}</Alert>}
            {pendingRange && (
              <Alert variant="light" className="border" style={{ borderRadius: '12px' }}>
                <strong>{formatDate(pendingRange.start)}</strong>
                {pendingRange.start !== pendingRange.end && <> – <strong>{formatDate(pendingRange.end)}</strong></>}
              </Alert>
            )}

            <Form.Group className="mb-3">
              <Form.Label className="fw-medium">Rodzaj urlopu</Form.Label>
              <div className="d-flex gap-2">
                {Object.entries(VACATION_TYPES).map(([key, val]) => (
                  <div
                    key={key}
                    onClick={() => setVacType(key)}
                    className="flex-fill p-2 text-center d-flex align-items-center justify-content-center gap-2"
                    style={{
                      borderRadius: '12px', cursor: 'pointer', fontSize: '0.85rem',
                      border: vacType === key ? `2px solid ${val.color}` : '2px solid #e9ecef',
                      background: vacType === key ? val.bg : 'transparent',
                      color: vacType === key ? val.color : 'var(--pub-text)',
                      fontWeight: vacType === key ? 700 : 400,
                    }}
                  >
                    <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: val.color, display: 'inline-block', flexShrink: 0 }} />
                    {val.label}
                  </div>
                ))}
              </div>
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label className="fw-medium">Notatki (opcjonalnie)</Form.Label>
              <Form.Control
                as="textarea" rows={2} value={notes}
                onChange={e => setNotes(e.target.value)} style={{ borderRadius: '12px' }}
              />
            </Form.Group>

            <div className="d-flex justify-content-end gap-2">
              <button
                type="button" className="btn btn-light"
                onClick={() => { setShowAddModal(false); setPendingRange(null); }}
                style={{ borderRadius: '12px' }}
              >
                Anuluj
              </button>
              <GradientButton gradient="blue" onClick={handleConfirmAdd}>
                <FaPlus className="me-2" /> Dodaj urlop
              </GradientButton>
            </div>
          </Modal.Body>
        </Modal>

        {/* MODAL USUWANIA */}
        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
          <Modal.Header closeButton style={{ border: 'none' }}>
            <Modal.Title>Usuń urlop</Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            <p>Czy na pewno chcesz usunąć ten urlop?</p>
            {vacationToDelete && (
              <p className="text-muted">
                {formatDate(vacationToDelete.start_date)}
                {vacationToDelete.start_date !== vacationToDelete.end_date && ` – ${formatDate(vacationToDelete.end_date)}`}
              </p>
            )}
          </Modal.Body>
          <Modal.Footer style={{ border: 'none' }}>
            <button className="btn btn-light" onClick={() => setShowDeleteModal(false)} style={{ borderRadius: '12px' }}>
              Anuluj
            </button>
            <button className="btn btn-danger" onClick={handleConfirmDelete} style={{ borderRadius: '12px' }}>
              Usuń
            </button>
          </Modal.Footer>
        </Modal>

      </Container>
    </div>
  );
}

export default EmployerVacations;